/**
 * <s-product-gallery> — PDP media carousel with thumbnail strip.
 *
 * The track is a native scroll-snap container, so touch swipe, trackpad and
 * keyboard scrolling work without JS. This component only adds:
 *   - prev/next buttons that move one slide,
 *   - thumbnails that jump to their slide,
 *   - aria-current on the thumbnail of the visible slide.
 *
 * Required markup:
 *   [data-gallery-track]          — horizontal scroll-snap container.
 *   [data-gallery-slide]          — one per media item, direct children of the track.
 *   [data-gallery-thumb][data-index] — optional thumbnail buttons.
 *   [data-gallery-thumbs]         — optional scrollable thumbnail strip.
 *   [data-gallery-prev], [data-gallery-next] — optional arrow buttons.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #frame = 0;
  #active = -1;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('click', this.#handleClick, opts);
    this.#track?.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #track() {
    return this.querySelector('[data-gallery-track]');
  }

  get #slides() {
    return Array.from(this.querySelectorAll('[data-gallery-slide]'));
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb && this.contains(thumb)) {
      this.#goTo(Number(thumb.getAttribute('data-index')));
      return;
    }
    if (event.target.closest('[data-gallery-prev]')) this.#goTo(this.#currentIndex() - 1);
    if (event.target.closest('[data-gallery-next]')) this.#goTo(this.#currentIndex() + 1);
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  #currentIndex() {
    const track = this.#track;
    if (!track || track.clientWidth === 0) return 0;
    return Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
  }

  #goTo(index) {
    const track = this.#track;
    const slides = this.#slides;
    if (!track || slides.length === 0) return;

    const clamped = Math.max(0, Math.min(slides.length - 1, index));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollTo({ left: direction * clamped * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  #sync() {
    const index = this.#currentIndex();
    if (index === this.#active) return;
    this.#active = index;

    const slides = this.#slides;
    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = index <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = index >= slides.length - 1;

    const strip = this.querySelector('[data-gallery-thumbs]');
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      const isActive = Number(thumb.getAttribute('data-index')) === index;
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
        // Keep the active thumbnail visible by scrolling only the strip, never the page.
        if (strip instanceof HTMLElement && thumb instanceof HTMLElement) {
          const start = thumb.offsetLeft - strip.offsetLeft;
          const end = start + thumb.offsetWidth;
          if (start < strip.scrollLeft) strip.scrollLeft = start;
          else if (end > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = end - strip.clientWidth;
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
